import { Link } from 'react-router-dom';
import { Download, ShieldCheck, FileCheck, Building2, Handshake, Check } from 'lucide-react';
import { SITE, FAQS, EXPERIENCE, IMG } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal, SpotlightImage } from '../components/motion';
import { PageHero, Breadcrumbs } from '../components/chrome';
import { FAQ } from '../components/FAQ';
import { RFQForm } from '../components/RFQForm';

const crumbs = [{ label: 'Home', href: '/' }, { label: 'Government' }];

const CAPABILITIES = [
  ['Federal procurement capability', 'Registered and active in SAM.gov with UEI and CAGE codes, NAICS/PSC mapping, and documentation aligned to federal purchasing formats.'],
  ['Purchase order capability', 'Purchase orders with net terms, quoted to the format your contracting office expects.'],
  ['Government purchase card capability', 'Purchase card payments accepted for micro-purchases and urgent requirements.'],
  ['Prime contractor support', 'Equipment sourcing, installation, and lifecycle scopes executed as prime.'],
  ['Subcontractor support', 'Specialized analytical-equipment scopes for primes — quoted, documented, and delivered to your program schedule.'],
  ['OEM/distributor sourcing', 'Brand-specific instrumentation sourced through established OEM and authorized-distributor channels.'],
  ['RFQ response capability', 'Formatted quotes with compliance paperwork — Section 889 representation, delivery terms, and line-item detail.'],
  ['Equipment installation & commissioning', 'Delivery, installation, configuration, performance verification, and acceptance documentation nationwide.'],
  ['Documentation & acceptance support', 'IQ/OQ/PQ coordination, traceable calibration records, and turnover packages built for audit.'],
  ['Nationwide service capability', 'Remote triage with next-business-day target and coordinated on-site service across all 50 states.'],
];

export default function Government() {
  return (
    <div data-testid="page-government">
      <Seo
        title="Government Contractor: Laboratory Equipment & Analytical Instrumentation | ToxGlobal Analytics"
        description="Government-ready analytical equipment contractor. SAM.gov Active — UEI J9B2HR6YNKQ5, CAGE 76S34. RFQ response, purchase card and PO fulfillment, installation, commissioning, and nationwide support."
        path="/government"
        breadcrumbs={crumbs}
      />

      <PageHero
        dark
        eyebrow="Government contracting"
        lines="Equipment for the missions that can't wait."
        sub="ToxGlobal Analytics supports contracting officers, procurement specialists, program managers, laboratory directors, scientists, and prime contractors with analytical equipment procurement, installation, commissioning, and lifecycle support — as prime or subcontractor."
        breadcrumbs={crumbs}
        image={IMG.govLab}
        imageAlt="Federal laboratory facility equipped with analytical instrumentation"
      />

      <section className="section-pad" data-testid="government-snapshot">
        <div className="container-x">
          <Reveal>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <h2 className="display text-3xl sm:text-4xl text-ink">Contracting snapshot</h2>
              <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1.5" data-testid="sam-active-badge">
                <ShieldCheck size={13} /> SAM.gov Active
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="border border-slate-300 bg-white">
              <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-slate-200 border-b border-slate-200">
                {[
                  ['UEI', SITE.uei], ['CAGE', SITE.cage], ['SAM.gov', 'Active'], ['Small Business', 'SDMB'],
                ].map(([k, v]) => (
                  <div key={k} className="p-5" data-testid={`snapshot-${k.toLowerCase().replace(/\s/g, '-')}`}>
                    <p className="eyebrow text-slate-400">{k}</p>
                    <p className="mt-1.5 font-display font-semibold text-lg text-ink tabular">{v}</p>
                  </div>
                ))}
              </div>
              <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
                <div className="p-6">
                  <p className="eyebrow text-signal mb-4">NAICS codes</p>
                  <ul className="space-y-2.5">
                    {SITE.naics.map(([code, label, tag]) => (
                      <li key={code} className="flex gap-3 text-sm" data-testid={`naics-${code}`}>
                        <span className="font-mono text-ink tabular w-16 shrink-0">{code}</span>
                        <span className="text-slate-600">{label} {tag && <span className="ml-1 font-mono text-[10px] uppercase tracking-wider text-signal border border-signal/40 px-1.5 py-0.5">{tag}</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-6">
                  <p className="eyebrow text-signal mb-4">PSC codes</p>
                  <ul className="space-y-2.5">
                    {SITE.psc.map(([code, label, tag]) => (
                      <li key={code} className="flex gap-3 text-sm" data-testid={`psc-${code}`}>
                        <span className="font-mono text-ink tabular w-16 shrink-0">{code}</span>
                        <span className="text-slate-600">{label} {tag && <span className="ml-1 font-mono text-[10px] uppercase tracking-wider text-signal border border-signal/40 px-1.5 py-0.5">{tag}</span>}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="eyebrow text-signal mt-6 mb-3">Accepted payment</p>
                  <div className="flex flex-wrap gap-2">
                    {SITE.payment.map((p) => <span key={p} className="bg-slate-100 px-3 py-1.5 text-xs text-slate-700">{p}</span>)}
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 p-6 bg-slate-50 flex flex-wrap items-center gap-3">
                <Link to="/contact" className="btn-primary" data-testid="gov-cta-request-quote">Request a Government Quote</Link>
                <Link to="/contact" className="btn-ghost" data-testid="gov-cta-send-rfq">Send an RFQ</Link>
                <a href="/capability-statement.pdf" download className="btn-ghost" data-testid="gov-capability-download"><Download size={15} /> Capability Statement</a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-xs text-slate-500 max-w-3xl">
              Small business classification shown reflects currently verified status. Compliance items: Section 889 representation
              available on request; 21 CFR Part 11-ready configurations; GxP documentation on request.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white section-pad hairline-t" data-testid="government-capabilities">
        <div className="container-x">
          <Reveal><p className="eyebrow text-signal mb-3">Capability</p><h2 className="display text-3xl sm:text-5xl text-ink mb-10 max-w-3xl">What contracting teams can rely on</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {CAPABILITIES.map(([t, d], i) => (
              <Reveal key={t} delay={Math.min(i * 0.03, 0.25)} className="bg-white">
                <div className="p-6 sm:p-7 h-full hover:bg-slate-50 transition-colors" data-testid={`gov-capability-${i}`}>
                  <p className="font-mono text-[11px] text-slate-400 tabular">{String(i + 1).padStart(2, '0')}</p>
                  <h3 className="mt-2 font-display font-semibold text-ink">{t}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="government-past-performance">
        <div className="container-x grid lg:grid-cols-[1.1fr_0.9fr] gap-12">
          <div>
            <Reveal>
              <p className="eyebrow text-signal mb-3">Past performance</p>
              <h2 className="display text-3xl sm:text-4xl text-ink">Substantiated experience, stated carefully</h2>
              <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
                ToxGlobal references only engagements it can substantiate. Contract-specific details are available on request
                and shared appropriately during teaming or proposal processes.
              </p>
            </Reveal>
            <div className="mt-8 border-t border-slate-200">
              {EXPERIENCE.map((x, i) => (
                <Reveal key={x.org} delay={i * 0.05}>
                  <div className="py-5 border-b border-slate-200 flex gap-4">
                    <FileCheck size={18} className="text-signal mt-1 shrink-0" />
                    <div>
                      <h3 className="font-display font-semibold text-ink">{x.org}</h3>
                      <p className="mt-1 text-sm text-slate-600">{x.detail}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="space-y-6">
            <Reveal delay={0.05}>
              <div className="spotlight-dark grain text-white p-8" data-testid="capability-statement-card">
                <Building2 size={22} className="text-signal" />
                <h3 className="display text-2xl mt-4">Capability Statement</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  A one-page PDF with UEI, CAGE, NAICS/PSC codes, capability summary, and contact details — formatted for
                  contracting file submissions. Download it here or request a signed copy by email.
                </p>
                <a href="/capability-statement.pdf" download className="btn-light w-full mt-5" data-testid="government-capability-statement-download">
                  <Download size={15} /> Download Capability Statement
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-slate-300 bg-white p-8" data-testid="contract-vehicles-card">
                <Handshake size={22} className="text-signal" />
                <h3 className="display text-2xl mt-4 text-ink">Contract vehicles & teaming</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  ToxGlobal fulfills requirements via open-market purchases, RFQs, and micro-purchases today, and supports
                  prime contractors as a specialized subcontractor for analytical-equipment scopes. Contract-vehicle
                  participation is updated as vehicles are awarded.
                </p>
                <ul className="mt-4 space-y-2">
                  {['Open market / GPC / PO', 'Subcontractor teaming available', 'OEM-authorized service coordination'].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-slate-700"><Check size={14} className="text-signal" /> {t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <FAQ faqs={FAQS} eyebrow="Contracting FAQ" title="Questions contracting officers ask" />

      <section className="section-pad bg-slate-50 hairline-t" data-testid="government-rfq">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-signal mb-3">RFQ intake</p>
            <h2 className="display text-3xl sm:text-4xl text-ink mb-8">Send your requirement</h2>
          </Reveal>
          <RFQForm />
        </div>
      </section>
    </div>
  );
}
